'use client';

import { useState } from 'react';

import { useAllSpeciesAtLocation } from '@domain/regulations/hooks/useAllSpeciesAtLocation';
import {
  useIntersectionObserver,
  UseIntersectionObserverOptions,
} from '@hooks/useIntersectionObserver';
import { toNumber } from '@lib/strings/toNumber';
import { ICoordinates } from '@app-types/geo';
import { FishingWaterRegionFields, PlaceRegionFields } from '../../generated/graphql';
import { Heading } from '../Heading/Heading';
import { RegulationLocationCard } from '../RegulationLocationCard/RegulationLocationCard';
import { IOptionType, Select } from '../Select/Select';
import { StaticPlaceMap } from '../StaticPlaceMap/StaticPlaceMap';
import { Text } from '../Text/Text';

import { firstNonEmpty } from '@lib/strings/firstNonEmpty';
import { FetchedAtText } from './components/FetchedAtText';
import { NoRegulations } from './components/NoRegulations';
import { RegulationDisclaimer } from './components/RegulationDisclaimer';
import { RegulationDetails } from './components/RegulationDetails';
import { twMerge } from 'tailwind-merge';
import { Container } from '../Container/Container';
import { LicenseSection } from './components/LicenseSection';
import { SelectedSpeciesText } from './components/SelectedSpeciesText';
import { SpeciesRegulations } from '../../api/fishrules/getSpeciesRegulationsAtLocation';
import { Boundary } from '../../api/fishrules/getBoundariesAtLocation';
import { FeatureFlag } from '../FeatureFlag/FeatureFlag';
import { RegulationSectionNew } from './RegulationSectionNew';

const customStyles = {
  control: (provided: any) => ({
    ...provided,
    borderRadius: '8px',
    border: '1px solid #DDE3E8',
    height: '48px',
  }),
  dropdownIndicator: (provided: any) => ({
    ...provided,
    color: '#3F5162',
    fontSize: '2px',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  menu: (provided: any) => ({
    ...provided,
  }),
};

export interface RegulationInfo {
  boundaryName?: string;
  fetchedAt?: string;
  isLoading: boolean;
}

export interface IRegulationSectionProps {
  boundariesAtLocation?: Boundary[];
  coordinates: ICoordinates;
  initialRegulations?: SpeciesRegulations;
  speciesRegulations?: SpeciesRegulations[];
  speciesIdsFilter?: string[];
  region: FishingWaterRegionFields | PlaceRegionFields | undefined;
  title?: string;
  className?: string;
  showStaticMap?: boolean;
  showLicenceInfo?: boolean;
  intersectionObserverOptions?: UseIntersectionObserverOptions;
  id?: string;
  enableNewVariant?: boolean;
  appDownloadModalTitle?: string;
}

export const RegulationSection = ({
  boundariesAtLocation,
  coordinates,
  initialRegulations,
  speciesRegulations,
  speciesIdsFilter,
  region,
  title = 'Regulations',
  className,
  showStaticMap = false,
  showLicenceInfo = false,
  intersectionObserverOptions = { rootMargin: '500px' },
  id = 'regulations',
  enableNewVariant = false,
  appDownloadModalTitle,
}: IRegulationSectionProps) => {
  const oldComponent = (
    <RegulationSectionContent
      boundariesAtLocation={boundariesAtLocation}
      coordinates={coordinates}
      initialRegulations={initialRegulations}
      speciesIdsFilter={speciesIdsFilter}
      region={region}
      title={title}
      className={className}
      showStaticMap={showStaticMap}
      showLicenceInfo={showLicenceInfo}
      intersectionObserverOptions={intersectionObserverOptions}
      id={id}
    />
  );

  if (!enableNewVariant) {
    return oldComponent;
  }

  return (
    <FeatureFlag
      featureFlag="regulations-refactor"
      oldComponent={oldComponent}
      newComponent={
        <RegulationSectionNew
          id={id}
          title={title}
          className={className}
          region={region}
          coordinates={coordinates}
          showStaticMap={showStaticMap}
          showLicenceInfo={showLicenceInfo}
          speciesRegulations={speciesRegulations}
          boundariesAtLocation={boundariesAtLocation}
          appDownloadModalTitle={appDownloadModalTitle}
        />
      }
    />
  );
};

const RegulationSectionContent = ({
  boundariesAtLocation,
  coordinates,
  initialRegulations,
  speciesIdsFilter,
  region,
  title = 'Regulations',
  className,
  showStaticMap = false,
  showLicenceInfo = false,
  intersectionObserverOptions = { rootMargin: '500px' },
  id = 'regulations',
}: Omit<IRegulationSectionProps, 'enableNewVariant'>) => {
  const [regulationInfo, setRegulationInfo] = useState<RegulationInfo>({
    boundaryName: initialRegulations?.regulations.location_name,
    fetchedAt: initialRegulations?.fetchedAt,
    isLoading: false,
  });
  const [userSelectedSpecies, setUserSelectedSpecies] = useState<IOptionType | undefined>();
  const [isInView, ref] = useIntersectionObserver(intersectionObserverOptions);

  const { isLoading: isLoadingAllSpecies, allSpecies } = useAllSpeciesAtLocation(
    isInView ? coordinates : undefined,
    speciesIdsFilter,
  );

  const allSpeciesOptions = allSpecies.map(({ name, fishRulesId }) => ({
    value: fishRulesId.toString(),
    label: name,
  }));

  const initialSpeciesOption: IOptionType | undefined = initialRegulations
    ? {
        label: initialRegulations.displayName,
        value: initialRegulations.regulations.fish_id.toString(),
      }
    : allSpeciesOptions[0];

  const selectedSpecies = userSelectedSpecies ?? initialSpeciesOption;
  const selectedSpeciesId = toNumber(selectedSpecies?.value);

  const hasLicenceInfo = region?.licenseInfoUrl;

  return (
    <div
      className="bg-lake-fog scroll-mt-(--navigation-scrolltop-offset-mobile) sm:scroll-mt-(--navigation-scrolltop-offset)"
      ref={ref}
      id={id}
      data-testid="Regulations"
    >
      <Container className={twMerge('py-sm', className)}>
        <Heading as="h2" variant="sm" className="mb-8">
          {title}
        </Heading>
        <div className="grid grid-cols-4 gap-3 md:grid-cols-12 md:gap-4">
          {!selectedSpecies && !regulationInfo.isLoading ? (
            <div className="order-2 col-span-4 md:order-1 md:col-span-6">
              <RegulationDisclaimer />
              <NoRegulations />
            </div>
          ) : (
            <div className="order-2 col-span-4 md:order-1 md:col-span-6">
              <RegulationDisclaimer />
              <Text>
                Fishing regulations{' '}
                {region ? `in ${firstNonEmpty(region.localName, region.name)}` : ''} can change
                throughout the year. Make sure to check this page before fishing for the most up to
                date rules and regulations for the current season. Local regulations govern when you
                can fish, the max size of the fish you can keep, how many fish you can keep, and
                more.
              </Text>
              <Text>
                Below you will see fishing regulations for catching{' '}
                <SelectedSpeciesText
                  isLoading={regulationInfo.isLoading}
                  selectedSpeciesName={
                    selectedSpecies ? (selectedSpecies as { label: string }).label : 'species'
                  }
                />{' '}
                as of{' '}
                <FetchedAtText
                  isLoading={regulationInfo.isLoading}
                  fetchedAt={regulationInfo.fetchedAt}
                />
                . To view regulations for a different fish species, please click on your preferred
                species in the drop-down.
              </Text>
              <Select
                isDisabled={isLoadingAllSpecies}
                id="fishing-water-rules-species-select"
                inputId="fishing-water-rules-species-select-input"
                value={selectedSpecies}
                options={allSpeciesOptions}
                onChange={(newVal: unknown) => {
                  setUserSelectedSpecies(newVal as IOptionType);
                }}
                className="md:w-col-3 mt-1 sm:w-1/2"
                label="Select species"
                styles={customStyles}
                aria-label="Select Species"
              />
              <hr className="text-twilight my-10" />
              <RegulationDetails
                boundaries={boundariesAtLocation}
                coordinates={coordinates}
                selectedSpeciesId={selectedSpeciesId}
                initialRegulations={initialRegulations?.regulations}
                onRegulationChange={setRegulationInfo}
              />
            </div>
          )}
          <div className="order-1 col-span-4 col-start-1 mb-3 sm:col-span-3 sm:mb-8 md:top-0 md:order-2 md:col-span-5 md:col-start-8">
            <div className="sticky top-36">
              <RegulationLocationCard
                coordinates={coordinates}
                boundaryName={regulationInfo.boundaryName}
                boundaries={boundariesAtLocation}
                fishrulesSpeciesId={selectedSpeciesId}
                className="relative rounded-sm"
                isLoadingBoundaries={regulationInfo.isLoading}
              />
              {showLicenceInfo && hasLicenceInfo && (
                <LicenseSection
                  className="hidden bg-white md:mx-0 md:mt-8 md:flex md:max-w-none"
                  region={region}
                />
              )}
              {showStaticMap && <StaticPlaceMap coordinates={coordinates} />}
            </div>
          </div>
        </div>
      </Container>
      {showLicenceInfo && hasLicenceInfo && (
        <Container className="mb-md sm:mt-md bg-white py-12 md:hidden">
          <LicenseSection region={region} />
        </Container>
      )}
    </div>
  );
};
